import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs'
import { overlayStack } from '@schmancy/utils/overlay-stack'
import type { WindowBounds, WindowRecord, WindowRegistryState, SnapTarget } from './window-registry.js'

const STORAGE_PREFIX = 'schmancy-window-'
const LEGACY_STORAGE_PREFIX = 'schmancy-float-'

class WindowManagerService {
	private static instance: WindowManagerService

	readonly #state$ = new BehaviorSubject<WindowRegistryState>({
		windows: new Map(),
		focusedId: null,
		stackOrder: [],
	})

	readonly state$ = this.#state$.asObservable()

	private constructor() {}

	static getInstance(): WindowManagerService {
		if (!WindowManagerService.instance) {
			WindowManagerService.instance = new WindowManagerService()
		}
		return WindowManagerService.instance
	}

	// ── Synchronous accessors ──────────────────────────────────────────

	get windows(): Map<string, WindowRecord> {
		return this.#state$.value.windows
	}

	get focusedId(): string | null {
		return this.#state$.value.focusedId
	}

	// ── Registration ───────────────────────────────────────────────────

	register(id: string, initialBounds: WindowBounds, snapTarget: SnapTarget, label = '', hidden = false): void {
		const state = this.#state$.value
		if (state.windows.has(id)) return

		const zIndex = overlayStack.assignZIndex(id)
		const record: WindowRecord = {
			id,
			label,
			hidden,
			bounds: initialBounds,
			visualState: 'normal',
			zIndex,
			open: false,
			snapTarget,
		}

		const windows = new Map(state.windows)
		windows.set(id, record)
		const stackOrder = overlayStack.getStackOrder()

		this.#state$.next({ ...state, windows, stackOrder })
	}

	unregister(id: string): void {
		const state = this.#state$.value
		if (!state.windows.has(id)) return

		overlayStack.releaseId(id)
		const windows = new Map(state.windows)
		windows.delete(id)
		const stackOrder = overlayStack.getStackOrder()
		const focusedId = state.focusedId === id ? null : state.focusedId

		this.#state$.next({ ...state, windows, stackOrder, focusedId })
	}

	// ── State mutations ────────────────────────────────────────────────

	updateBounds(id: string, bounds: WindowBounds): void {
		this.updateRecord(id, { bounds })
	}

	updateVisualState(id: string, visualState: WindowRecord['visualState']): void {
		this.updateRecord(id, { visualState })
	}

	updateSnapTarget(id: string, snapTarget: SnapTarget): void {
		this.updateRecord(id, { snapTarget })
	}

	updateHidden(id: string, hidden: boolean): void {
		this.updateRecord(id, { hidden })
	}

	updateLabel(id: string, label: string): void {
		this.updateRecord(id, { label })
	}

	activate(id: string): void {
		this.focus(id)
		this.updateOpen(id, true)
	}

	updateOpen(id: string, open: boolean): void {
		const record = this.windows.get(id)
		this.updateRecord(id, { open })
		if (!open && record?.open && record.snapTarget !== 'free') {
			const previous = [...this.windows.values()].filter(member => member.id !== id && !member.hidden && member.snapTarget === record.snapTarget).sort((a, b) => b.zIndex - a.zIndex)[0]
			if (previous) this.focus(previous.id)
		}
	}

	focus(id: string): void {
		const state = this.#state$.value
		if (!state.windows.has(id)) return

		const zIndex = overlayStack.bringToFront(id)
		const windows = new Map(state.windows)
		const record = windows.get(id)
		if (record) {
			windows.set(id, { ...record, zIndex })
		}
		const stackOrder = overlayStack.getStackOrder()

		this.#state$.next({ ...state, windows, stackOrder, focusedId: id })
	}

	// ── Queries ────────────────────────────────────────────────────────

	findOverlaps(bounds: WindowBounds, excludeId: string): WindowRecord[] {
		const result: WindowRecord[] = []
		for (const [id, record] of this.#state$.value.windows) {
			if (id === excludeId) continue
			if (rectsOverlap(bounds, record.bounds)) {
				result.push(record)
			}
		}
		return result
	}

	getNeighbors(id: string): WindowRecord[] {
		const result: WindowRecord[] = []
		for (const [otherId, record] of this.#state$.value.windows) {
			if (otherId !== id) result.push(record)
		}
		return result
	}

	// ── Selectors ──────────────────────────────────────────────────────

	selectWindow(id: string): Observable<WindowRecord | undefined> {
		return this.#state$.pipe(
			map(state => state.windows.get(id)),
			distinctUntilChanged(),
		)
	}

	selectFocused(): Observable<string | null> {
		return this.#state$.pipe(
			map(state => state.focusedId),
			distinctUntilChanged(),
		)
	}

	// ── Persistence ────────────────────────────────────────────────────

	loadPosition(id: string): { x: number; y: number; anchor: string; snapTarget?: SnapTarget } | null {
		try {
			const raw = localStorage.getItem(STORAGE_PREFIX + id) ?? localStorage.getItem(LEGACY_STORAGE_PREFIX + id)
			if (!raw) return null
			return JSON.parse(raw) as { x: number; y: number; anchor: string; snapTarget?: SnapTarget }
		} catch {
			return null
		}
	}

	savePosition(id: string, data: { x: number; y: number; anchor: string; snapTarget?: SnapTarget }): void {
		try {
			localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(data))
		} catch {}
	}

	clearPosition(id: string): void {
		try {
			localStorage.removeItem(STORAGE_PREFIX + id)
			localStorage.removeItem(LEGACY_STORAGE_PREFIX + id)
		} catch {}
	}

	// ── Internal ───────────────────────────────────────────────────────

	private updateRecord(id: string, updates: Partial<WindowRecord>): void {
		const state = this.#state$.value
		const record = state.windows.get(id)
		if (!record) return

		const windows = new Map(state.windows)
		windows.set(id, { ...record, ...updates })
		this.#state$.next({ ...state, windows })
	}
}

/** Duplicated from window-position.ts to avoid a circular import. */
function rectsOverlap(a: WindowBounds, b: WindowBounds): boolean {
	return !(
		a.left >= b.left + b.width ||
		a.left + a.width <= b.left ||
		a.top >= b.top + b.height ||
		a.top + a.height <= b.top
	)
}

export const windowManager = WindowManagerService.getInstance()
